import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';

/*
 * Search-match highlighting. The Search API wraps matches in `<mark>`; `Highlight`
 * restyles those runs. `markQuery` produces the same runs for searches with no
 * backend highlighter, so every path renders identical marks.
 *
 * Nothing is parsed as HTML: only a bare `<mark>` has meaning, so the API's
 * highlighter must stay on the plain tag — `<mark class="…">` renders as text.
 */

// `[\s\S]` rather than `.` so a run spanning a newline still closes.
const markedRun = /<mark>([\s\S]*?)<\/mark>/g;

function renderMarkedText(text: string): ReactNode[] {
    const parts: ReactNode[] = [];
    let cursor = 0;

    markedRun.lastIndex = 0;
    let run = markedRun.exec(text);

    while (run !== null) {
        if (run.index > cursor) {
            parts.push(text.slice(cursor, run.index));
        }

        // An empty `<mark></mark>` would render as a bare swatch.
        if (run[1] !== '') {
            parts.push(
                <mark
                    key={parts.length}
                    data-slot="highlight-mark"
                    className="rounded-xs bg-highlight text-inherit"
                >
                    {run[1]}
                </mark>,
            );
        }
        cursor = run.index + run[0].length;
        run = markedRun.exec(text);
    }

    if (cursor < text.length) {
        parts.push(text.slice(cursor));
    }

    return parts;
}

export type HighlightProps = ComponentPropsWithRef<'span'> & {
    /** Display text with matches wrapped in `<mark>`. Text without one renders
     * unchanged, so every row can use this whether it matched or not. */
    text: string;
};

export function Highlight({ text, ...props }: HighlightProps): ReactElement {
    return (
        <span data-slot="highlight" {...props}>
            {renderMarkedText(text)}
        </span>
    );
}

export type MarkQueryOptions = {
    /** Mark only the first occurrence. The filter convention; `false` for result lists. */
    firstOnly?: boolean;
    /** Shortest query that marks anything. `2` for result lists, so a single letter
     * doesn't light up nearly every word. */
    minLength?: number;
    /**
     * Trim `text` to the first match plus this many characters either side, with `…` on
     * each cut end. Omit to keep the whole string. For long bodies like note texts,
     * where the match can sit past the point CSS truncation would cut; with no match,
     * keeps the head.
     *
     * Only the *first* match is centred — later ones are cut away with the tail and
     * nothing signals they existed, so `firstOnly: false` marks only what survives.
     *
     * @example
     * markQuery('Lieferung KW 29 zugesagt, Skonto bleibt unverändert', 'Skonto', { snippetRadius: 16 });
     * // '… KW 29 zugesagt, <mark>Skonto</mark> bleibt unveränd …'
     */
    snippetRadius?: number;
};

/**
 * Wraps matches of `query` in `<mark>` for `Highlight`, for searches with no backend
 * highlighter. Case-insensitive substring match on the trimmed query.
 *
 * Snipping happens *before* marking, so the slice can never cut a `<mark>` in half. A
 * literal `<mark>` already in `text` is read as markup — the same ambiguity the Search
 * API's own wrapping has.
 */
export function markQuery(
    text: string,
    query: string,
    { firstOnly = true, minLength = 1, snippetRadius }: MarkQueryOptions = {},
): string {
    const trimmed = query.trim();
    const needle = trimmed.toLowerCase();
    // Below `minLength` there is nothing to mark and nothing to centre on, so it
    // behaves exactly like a query that found nothing.
    const qualifies = trimmed.length > 0 && trimmed.length >= minLength;
    const firstMatch = qualifies ? text.toLowerCase().indexOf(needle) : -1;

    let snippet = text;
    let opening = '';
    let closing = '';

    if (snippetRadius !== undefined) {
        const start = firstMatch === -1 ? 0 : Math.max(0, firstMatch - snippetRadius);
        const end =
            firstMatch === -1
                ? Math.min(text.length, snippetRadius * 2)
                : Math.min(text.length, firstMatch + needle.length + snippetRadius);

        snippet = text.slice(start, end);

        // Trim clipped edges only, so the ellipsis doesn't keep the whitespace the cut
        // started on ("…  nach" → "… nach").
        if (start > 0) {
            snippet = snippet.replace(/^\s+/, '');
            opening = '… ';
        }
        if (end < text.length) {
            snippet = snippet.replace(/\s+$/, '');
            closing = ' …';
        }
    }

    if (firstMatch === -1) {
        return opening + snippet + closing;
    }

    // Re-scan: `firstMatch` indexed into the full text, not the snippet.
    const haystack = snippet.toLowerCase();
    let match = haystack.indexOf(needle);
    let marked = '';
    let cursor = 0;

    while (match !== -1) {
        marked += `${snippet.slice(cursor, match)}<mark>${snippet.slice(match, match + needle.length)}</mark>`;
        cursor = match + needle.length;
        match = firstOnly ? -1 : haystack.indexOf(needle, cursor);
    }

    return opening + marked + snippet.slice(cursor) + closing;
}
